import { Component, type ReactNode } from 'react'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import { Button } from '@/components/ui/button'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component for catching React errors
 * Wraps Sentry's ErrorBoundary for automatic error reporting
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Error is automatically sent to Sentry via SentryErrorBoundary
    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="bg-card p-8 rounded-lg shadow-md border max-w-md w-full">
            <h1 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              An error occurred in the application. The error has been reported to our team.
            </p>
            {this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-muted-foreground mb-2">
                  Error details
                </summary>
                <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            <Button onClick={this.handleReset} variant="default">
              Try again
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Sentry-enabled Error Boundary wrapper
 * Use this to wrap your app and automatically report errors to Sentry
 */
export function SentryErrorBoundaryWrapper({ children }: { children: ReactNode }) {
  return (
    <SentryErrorBoundary
      fallback={({ error, resetError }) => {
        const errorMessage =
          error instanceof Error ? error.message : String(error ?? 'Unknown error')
        const hasError = error !== null && error !== undefined
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="bg-card p-8 rounded-lg shadow-md border max-w-md w-full">
              <h1 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h1>
              <p className="text-muted-foreground mb-4">
                An error occurred in the application. The error has been reported to our team.
              </p>
              {hasError && (
                <details className="mb-4">
                  <summary className="cursor-pointer text-sm text-muted-foreground mb-2">
                    Error details
                  </summary>
                  <pre className="text-xs bg-muted p-2 rounded overflow-auto">{errorMessage}</pre>
                </details>
              )}
              <Button onClick={resetError} variant="default">
                Try again
              </Button>
            </div>
          </div>
        )
      }}
      showDialog
    >
      {children}
    </SentryErrorBoundary>
  )
}
